/**
 * Render the report-print-area DOM node to a real, downloadable PDF.
 *
 * Previously the "Download PDF" button fired window.print() and relied
 * on the browser's native Save-as-PDF sink — cheap but the user had to
 * navigate the system print dialog and pick the right output
 * destination. Direct download via html2canvas + jspdf is what most
 * apps ship for "save this view as a PDF"; the bundle hit is ~250 KB.
 *
 * Multi-page handling: html2canvas produces a single tall canvas. If
 * the rendered report is longer than one A4 page, we slice the canvas
 * into page-height strips and emit a multi-page PDF. Page width is
 * scaled to A4; height ratio is preserved.
 */
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

interface DownloadOpts {
  /** Element to snapshot — typically the `.report-print-area` article. */
  element: HTMLElement;
  /** Suggested filename without extension. */
  filename: string;
}

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MARGIN_MM = 8;

export async function downloadReportPdf({
  element,
  filename,
}: DownloadOpts): Promise<void> {
  // Capture at 2x device pixel ratio so the PDF stays sharp on retina
  // screens / when zoomed. backgroundColor:#fff guards against the
  // article inheriting a transparent body color and rendering odd
  // grey edges.
  const canvas = await html2canvas(element, {
    scale: Math.max(2, window.devicePixelRatio || 1),
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
  });

  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const usableWidth = A4_WIDTH_MM - MARGIN_MM * 2;
  const usableHeight = A4_HEIGHT_MM - MARGIN_MM * 2;
  const pxPerMm = canvas.width / usableWidth;
  const pageHeightPx = Math.floor(usableHeight * pxPerMm);

  let renderedPx = 0;
  let pageNo = 0;
  while (renderedPx < canvas.height) {
    const sliceHeight = Math.min(pageHeightPx, canvas.height - renderedPx);
    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceHeight;
    const ctx = sliceCanvas.getContext("2d");
    if (!ctx) throw new Error("canvas 2d context unavailable");
    ctx.drawImage(
      canvas,
      0,
      renderedPx,
      canvas.width,
      sliceHeight,
      0,
      0,
      canvas.width,
      sliceHeight,
    );
    const dataUrl = sliceCanvas.toDataURL("image/png");
    if (pageNo > 0) pdf.addPage();
    const sliceHeightMm = sliceHeight / pxPerMm;
    pdf.addImage(
      dataUrl,
      "PNG",
      MARGIN_MM,
      MARGIN_MM,
      usableWidth,
      sliceHeightMm,
      undefined,
      "FAST",
    );
    renderedPx += sliceHeight;
    pageNo += 1;
  }

  pdf.save(`${filename}.pdf`);
}
